import React, { useEffect } from 'react';
import { Button, Form, Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { FactoryDto, FactoryInput } from '../../dto/factory';
import { toFactoryInput } from '../../dto/factory';

export interface FactoryFormModalProps {
  open: boolean;
  /** `null` → create mode. */
  factory: FactoryDto | null;
  submitting: boolean;
  /** Field-level server error for the name (e.g. duplicate name). */
  nameError?: string | null;
  onSubmit: (payload: FactoryInput) => void;
  onCancel: () => void;
}

const FactoryFormModal: React.FC<FactoryFormModalProps> = ({
  open,
  factory,
  submitting,
  nameError,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation(['factories', 'common']);
  const [form] = Form.useForm();
  const isEdit = Boolean(factory);

  // destroyOnHidden remounts the form on every open, so initialValues always
  // reflect the factory being edited (or an empty create form).
  const initialValues = {
    name: factory?.name ?? '',
    taxId: factory?.taxId ?? '',
    address: factory?.address ?? '',
    contactPerson: factory?.contactPerson ?? '',
    email: factory?.email ?? '',
    phone: factory?.phone ?? '',
    notes: factory?.notes ?? '',
  };

  useEffect(() => {
    if (nameError) {
      form.setFields([{ name: 'name', errors: [nameError] }]);
    }
  }, [nameError, form]);

  return (
    <Modal
      title={isEdit ? t('factories:modal.editTitle') : t('factories:modal.createTitle')}
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      width={600}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          {t('factories:form.cancel')}
        </Button>,
        <Button key="submit" type="primary" loading={submitting} onClick={() => form.submit()}>
          {isEdit ? t('factories:form.submitEdit') : t('factories:form.submitCreate')}
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary">{t('factories:modal.intro')}</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={initialValues}
        onFinish={(values) => onSubmit(toFactoryInput(values))}
      >
        <Form.Item
          name="name"
          label={t('factories:form.name')}
          rules={[{ required: true, whitespace: true, message: t('factories:form.nameRequired') }]}
        >
          <Input placeholder={t('factories:form.namePlaceholder')} maxLength={200} autoFocus />
        </Form.Item>
        <Form.Item name="taxId" label={t('factories:form.taxId')}>
          <Input placeholder={t('factories:form.taxIdPlaceholder')} maxLength={50} />
        </Form.Item>
        <Form.Item name="address" label={t('factories:form.address')}>
          <Input placeholder={t('factories:form.addressPlaceholder')} />
        </Form.Item>
        <Form.Item name="contactPerson" label={t('factories:form.contactPerson')}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label={t('factories:form.email')}
          rules={[{ type: 'email', message: t('factories:form.emailInvalid') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={t('factories:form.phone')}>
          <Input />
        </Form.Item>
        <Form.Item name="notes" label={t('factories:form.notes')}>
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FactoryFormModal;
